import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Footer from '../../components/Footer/Footer';
import BannerImage from "../../assets/teachers.jpg";
import { MdGroups, MdSchool, MdPublic, MdSecurity, MdStars } from 'react-icons/md';
import { BsWhatsapp } from "react-icons/bs";
import { Link } from 'react-router-dom';
import { StaffLifeGallery } from '../../utils/galleryAssets';

const StaffLife = () => {
    const location = useLocation();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [location]);

    return (
        <div className="font-sans text-gray-900 overflow-x-hidden">
            

            {/* Hero Section */}
            <div className="relative h-[60vh] lg:h-[75vh] w-full flex items-center justify-center overflow-hidden bg-black">
                <img 
                    src={BannerImage} 
                    alt="Staff Banner" 
                    className="absolute inset-0 h-full w-full object-cover opacity-50 scale-105"
                />
                <div className="relative z-10 text-center px-5 max-w-5xl">
                    <h1 className="text-4xl md:text-7xl font-black text-white mb-6 uppercase tracking-tighter animate-fade-in">
                        Staff Life & Professional Culture
                    </h1>
                    <div className="w-24 h-2 bg-[#ed3137] mx-auto mb-8"></div>
                    <p className="text-xl md:text-2xl text-blue-50 font-medium max-w-3xl mx-auto leading-relaxed">
                        Excellence in education driven by a dedicated and professional team.
                    </p>
                </div>
            </div>

            {/* Introduction Section */}
            <section className="py-24 px-5 md:px-20 lg:px-40 bg-white">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="text-3xl md:text-5xl font-extrabold text-[#021d53] mb-10">A Committed Team</h2>
                    <p className="text-xl text-gray-600 leading-relaxed mb-10">
                        The strength of Anell lies in its people. Staff members work collaboratively to maintain a nurturing and high-performing environment. Professional relationships are built on respect, shared purpose, and dedication to excellence.
                    </p>
                    <div className="grid md:grid-cols-3 gap-8 text-left">
                        <div className="p-8 bg-gray-50 rounded-3xl group hover:bg-[#021d53] transition-all duration-500">
                            <MdGroups className="text-5xl text-[#ed3137] mb-6 group-hover:text-white" />
                            <h4 className="text-xl font-bold text-[#021d53] group-hover:text-white mb-4">Collaboration</h4>
                            <p className="text-gray-500 group-hover:text-blue-100 text-sm">Working together to achieve collective goals and support every learner.</p>
                        </div>
                        <div className="p-8 bg-gray-50 rounded-3xl group hover:bg-[#021d53] transition-all duration-500">
                            <MdSecurity className="text-5xl text-[#ed3137] mb-6 group-hover:text-white" />
                            <h4 className="text-xl font-bold text-[#021d53] group-hover:text-white mb-4">Respect</h4>
                            <p className="text-gray-500 group-hover:text-blue-100 text-sm">Fostering a culture of mutual respect and professional integrity.</p>
                        </div>
                        <div className="p-8 bg-gray-50 rounded-3xl group hover:bg-[#021d53] transition-all duration-500">
                            <MdStars className="text-5xl text-[#ed3137] mb-6 group-hover:text-white" />
                            <h4 className="text-xl font-bold text-[#021d53] group-hover:text-white mb-4">Excellence</h4>
                            <p className="text-gray-500 group-hover:text-blue-100 text-sm">A shared dedication to upholding the highest standards of education.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Professional Development - Alternating Section */}
            <section className="py-24 bg-gray-50 overflow-hidden">
                <div className="max-w-[1440px] mx-auto px-5 md:px-20">
                    <div className="flex flex-col lg:flex-row items-center gap-16">
                        <div className="lg:w-1/2 space-y-8">
                            <h2 className="text-4xl md:text-5xl font-black text-[#021d53] leading-tight">Professional Development</h2>
                            <p className="text-lg text-gray-600 leading-relaxed">
                                Continuous training and development ensure that educators remain effective, innovative, and responsive to learners’ needs. We invest in our staff's growth to maintain our position at the forefront of educational excellence.
                            </p>
                            <div className="flex flex-col gap-4">
                                <div className="flex items-center gap-4 bg-white p-6 rounded-2xl shadow-sm border-l-8 border-[#ed3137]">
                                    <MdSchool className="text-3xl text-[#ed3137]" />
                                    <span className="font-bold text-[#021d53]">Advanced Pedagogical Training</span>
                                </div>
                                <div className="flex items-center gap-4 bg-white p-6 rounded-2xl shadow-sm border-l-8 border-[#021d53]">
                                    <MdPublic className="text-3xl text-[#021d53]" />
                                    <span className="font-bold text-[#021d53]">Global Educational Standards</span>
                                </div>
                            </div>
                        </div>
                        <div className="lg:w-1/2 relative">
                            <div className="absolute -inset-4 bg-[#021d53] rounded-[3rem] rotate-3 opacity-10"></div>
                            <img 
                                src={BannerImage} 
                                alt="Professional Development" 
                                className="relative z-10 rounded-[3rem] shadow-2xl object-cover h-[500px] w-full"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* Community Engagement */}
            <section className="py-24 px-5 md:px-20 lg:px-40 bg-white">
                <div className="max-w-5xl mx-auto flex flex-col lg:flex-row-reverse items-center gap-16">
                    <div className="lg:w-1/2 space-y-8 text-right">
                        <h2 className="text-3xl md:text-5xl font-extrabold text-[#021d53]">Community Engagement</h2>
                        <p className="text-xl text-gray-600 leading-relaxed">
                            Staff actively participate in school initiatives, community outreach, and collaborative projects that reinforce the school’s mission and values. Beyond the classroom, we are builders of a stronger, more connected community.
                        </p>
                        <ul className="space-y-4 text-gray-700 font-medium">
                            <li className="flex items-center justify-end gap-3"><span className="w-2 h-2 bg-[#ed3137] rounded-full"></span> School Outreach Programs</li>
                            <li className="flex items-center justify-end gap-3"><span className="w-2 h-2 bg-[#ed3137] rounded-full"></span> Collaborative Initiatives</li>
                            <li className="flex items-center justify-end gap-3"><span className="w-2 h-2 bg-[#ed3137] rounded-full"></span> Value-Driven Leadership</li>
                        </ul>
                    </div>
                    <div className="lg:w-1/2 grid grid-cols-2 gap-4">
                        <div className="h-64 bg-gray-200 rounded-3xl overflow-hidden shadow-lg">
                             <img src={BannerImage} className="w-full h-full object-cover" alt="Engagement 1" />
                        </div>
                        <div className="h-64 bg-[#021d53] rounded-3xl flex items-center justify-center p-8 text-white">
                            <p className="text-xl font-bold italic text-center">"Reinforcing our mission through active presence."</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* The Anell Difference - Centered Masterpiece */}
            <section className="py-32 bg-[#221b2d] text-white relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-full opacity-5 pointer-events-none">
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-white rounded-full blur-[120px]"></div>
                </div>
                <div className="max-w-5xl mx-auto text-center px-5 relative z-10">
                    <span className="text-[#ed3137] font-black uppercase tracking-[0.5em] mb-6 block">The Anell Difference</span>
                    <h2 className="text-4xl md:text-6xl font-black mb-12 leading-tight">The Anell Experience</h2>
                    <div className="space-y-10 text-xl md:text-2xl text-blue-100 font-light leading-relaxed max-w-4xl mx-auto">
                        <p>
                            The Anell Experience is more than activities and events. It is the daily interaction between learners and educators. It is the sense of belonging within the house system. It is the confidence gained through leadership and performance. It is the partnership between parents and the school.
                        </p>
                        <p className="text-3xl md:text-4xl font-bold text-white italic">
                            "It is a culture where excellence is pursued, character is formed, and every learner is prepared not just for exams, but for life."
                        </p>
                    </div>
                </div>
            </section>

            {/* Final CTA/Footer Intro */}
            <section className="py-24 bg-white text-center">
                <h3 className="text-3xl font-bold text-[#021d53] mb-8">Ready to join our professional community?</h3>
                <Link to="/about/careers" className="inline-block bg-[#ed3137] text-white px-12 py-5 rounded-full font-bold text-xl hover:bg-[#021d53] transition-all duration-300 shadow-2xl hover:scale-105 active:scale-95 uppercase tracking-widest">
                    Carrers
                </Link>
            </section>

            {/* Picture Collage */}
            <section className="pb-24 px-5 md:px-10 lg:px-20 max-w-[1440px] mx-auto">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {StaffLifeGallery.map((img, index) => (
                        <div key={index} className="relative h-80 overflow-hidden rounded-3xl shadow-xl group">
                            <img 
                                src={img} 
                                alt={`Staff Life ${index + 1}`} 
                                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                            />
                            <div className="absolute inset-0 bg-[#021d53] opacity-0 group-hover:opacity-20 transition-opacity duration-500"></div>
                        </div>
                    ))}
                </div>
            </section>

            <Footer />
             
        </div>
    );
};

export default StaffLife;
